import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { CandleVisual } from "./CandleVisual";
import { GIFT_SET } from "@/config";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => (
  <section id="top" className="relative overflow-hidden pt-14 pb-20 md:pt-24 md:pb-28">
    <div aria-hidden className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-brandTerracotta/10 blur-3xl" />
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.05 }}
      className="relative max-w-6xl mx-auto px-4 sm:px-6 mb-10"
    >
      <a
        data-testid="diwali-banner"
        href="#order-calculator"
        className="flex items-center justify-center gap-2.5 bg-brandDarkSlate text-brandCream text-xs sm:text-sm font-medium px-5 py-3 rounded-full text-center hover:bg-black transition-colors"
      >
        <Sparkles className="w-4 h-4 text-brandTerracotta shrink-0" />
        <span>
          Diwali gifting is here — Festive Gift Set of {GIFT_SET.jars} at ₹{GIFT_SET.price.toLocaleString("en-IN")}{" "}
          <span className="text-brandTerracotta font-semibold">(save ₹{GIFT_SET.savings})</span>
        </span>
      </a>
    </motion.div>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-7 space-y-7 text-center lg:text-left">
        <motion.span
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="inline-block uppercase tracking-[0.25em] text-xs font-semibold px-4 py-1.5 rounded-full bg-brandSand text-brandTerracotta"
        >
          Pure Botanical Wellness
        </motion.span>
        <h1 data-testid="hero-heading" className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.12]">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" variants={lineVariants} custom={0} initial="hidden" animate="show">
              Breathe pure air.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span className="block italic font-normal text-brandTerracotta" variants={lineVariants} custom={1} initial="hidden" animate="show">
              Glow with tranquility.
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="text-base sm:text-lg text-brandDarkSlate/80 max-w-xl mx-auto lg:mx-0 leading-relaxed"
        >
          Handcrafted 5-inch perforated natural candle jars designed to soothe stress, circulate subtle therapeutic
          aromas, and illuminate your home with clean, paraffin-free warmth.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-2"
        >
          <motion.a
            data-testid="hero-order-cta"
            href="#order-calculator"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex justify-center items-center gap-2 bg-brandTerracotta text-white font-medium px-8 py-4 rounded-full shadow-md hover:shadow-xl hover:shadow-brandTerracotta/25 transition-shadow"
          >
            Select Quantity &amp; Order
          </motion.a>
          <motion.a
            data-testid="hero-explore-cta"
            href="#product-details"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex justify-center items-center px-8 py-4 rounded-full border border-brandDarkSlate/20 font-medium hover:bg-brandSand transition-colors"
          >
            Explore Benefits
          </motion.a>
        </motion.div>
      </div>
      <div className="lg:col-span-5 flex justify-center">
        <CandleVisual />
      </div>
    </div>
  </section>
);
